import { useState } from 'react';
import classNames from 'classnames';
import { RefreshCw } from 'lucide-react';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import { priceFormatSuperscript } from '@/lib/price';
import { isPurchaseFlowEnabled } from '@/lib/purchase-flow';
import { getTradeInRange, isTradeInAvailable } from '@/lib/trade-in';
import { priceFormater } from '@/settings';
import i18n from '@/settings/i18n';
import TradeInModal, { type TradeInViewT } from './TradeInModal';
import useTradeInQuote from './useTradeInQuote';

const t = i18n.init('buyback');

const RANGE = getTradeInRange();

const formatPrice = (value: number) =>
  priceFormatSuperscript(priceFormater.long(value));

/**
 * The trade-in in the cart's Summary, under the total, in the purchase flow
 * only: "Trade in and get $30-$800" until there's a trade-in, then what the
 * order comes to once it's paid out. Edit opens every trade-in, as on the PDP.
 * `hideWhenEmpty` (checkout) shows nothing until there's a trade-in.
 */
const CartTradeIn = ({
  total,
  hideWhenEmpty = false,
  className = 'mt-2',
}: {
  total: number;
  hideWhenEmpty?: boolean;
  className?: string;
}) => {
  const { customer } = useCustomerContext();
  const { quotes, remove } = useTradeInQuote();
  const [view, setView] = useState<TradeInViewT | null>(null);

  if (
    !isTradeInAvailable() ||
    !isPurchaseFlowEnabled(customer?.email) ||
    (hideWhenEmpty && quotes.length === 0)
  ) {
    return null;
  }

  const tradeInValue = quotes.reduce((sum, quote) => sum + quote.value, 0);

  return (
    <>
      <div
        className={classNames('rounded-lg bg-[#F7F7F7] px-4 py-3', className)}
      >
        {quotes.length > 0 ? (
          <>
            <div className="flex items-center justify-between gap-4 text-[13px] font-medium">
              <span className="leading-tight">{t`Total after trade-in`}</span>
              <span translate="no">
                {formatPrice(Math.max(total - tradeInValue, 0))}
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between gap-4 text-xs leading-tight text-gray-700/60">
              <span>
                {quotes.length > 1
                  ? t`${{
                      value: priceFormater.long(tradeInValue),
                    }} paid once your devices are received & inspected.`
                  : t`${{
                      value: priceFormater.long(tradeInValue),
                    }} paid once your device is received & inspected.`}
              </span>
              <button
                type="button"
                onClick={() => setView('offer')}
                className="shrink-0 font-medium text-gray-700 underline hover:text-gray-700/80"
              >
                {t`Edit`}
              </button>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between gap-4 text-xs">
            <span className="flex items-center gap-2">
              <RefreshCw className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
              {RANGE ? (
                <span>
                  {t`Trade in and get`} <span translate="no">{RANGE}</span>
                </span>
              ) : (
                t`Trade in your device`
              )}
            </span>
            <button
              type="button"
              onClick={() => setView('widget')}
              className="shrink-0 font-medium underline hover:text-gray-700/80"
            >
              {t`Add`}
            </button>
          </div>
        )}
      </div>
      <TradeInModal
        view={view}
        setView={setView}
        quotes={quotes}
        onRemove={remove}
      />
    </>
  );
};

export default CartTradeIn;
